import React from "react";
import { Flex, Spinner, Tooltip } from "@chakra-ui/react";
import { colours } from "@/utils/themeUtils";
import { ChakraFAIcon } from "@/components/ui/Icon";

interface Props {
	tooltipText: string;
	disabled: boolean;
	handleClick: () => void;
	icon: string;
	actionIsLoading?: boolean;
	/**
	 * This indicates that the button can reflect state and is not only a one-off action. When active, the button will be styled differently.
	 * This can be extended in the future to accommodate for other colours, for now all that's needed is red for Pause.
	 */
	isActive?: boolean;
	/**
	 * The data-testid attribute set for the button
	 */
	buttonTestId?: string;

	/**
	 * How the button should be styled when it's "active".
	 *
	 * Defaults to "blue-solid" if active and no variant is provided.
	 *
	 * The "blue-solid" variant is used for the focus button, "red-outline" is used for the pause button.
	 */
	activeVariant?: "blue-solid" | "red-outline";
}

export function StudentTileButton(props: Props): JSX.Element {
	function handleClick() {
		if (props.disabled) return;
		props.handleClick();
	}

	const activeBackground = props.activeVariant === "red-outline" ? colours.danger.background.medium : colours.blue[600];
	const activeColor = props.activeVariant === "red-outline" ? colours.danger.icon : "white";
	const activeBorder = props.activeVariant === "red-outline" ? `1px solid ${colours.danger.icon}88` : "1px solid transparent";

	return (
		<Tooltip label={props.tooltipText} isDisabled={props.actionIsLoading}>
			<Flex
				as={"button"}
				disabled={props.disabled || props.actionIsLoading}
				data-testid={props.buttonTestId}
				alignItems={"center"}
				justifyContent={"center"}
				grow={1}
				position={"relative"}
				height={"30px"}
				borderRadius={"md"}
				background={props.isActive ? activeBackground : colours.neutrals[50]}
				color={props.isActive ? activeColor : colours.navy[800]}
				border={props.isActive ? activeBorder : "1px solid transparent"}
				cursor={"pointer"}
				_hover={
					props.disabled
						? {}
						: { background: props.isActive ? activeBackground : colours.navy[700], color: props.isActive ? activeColor : "white" }
				}
				_disabled={{ cursor: "not-allowed", opacity: 0.5 }}
				onClick={handleClick}
			>
				{props.actionIsLoading ? (
					<Spinner color={"inherit"} width={"12px"} height={"12px"} />
				) : (
					<ChakraFAIcon icon={props.icon} variant={"solid"} color={"inherit"} width={"12px"} />
				)}
			</Flex>
		</Tooltip>
	);
}
